import {
  Component,
  Input,
  Renderer2,
  ViewChild,
  ElementRef,
  AfterViewInit,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  inject,
  OnDestroy,
  effect,
  TemplateRef
} from '@angular/core';
import { take, fromEvent, debounceTime, takeUntil } from 'rxjs';
import { FileService, LoggingService, AreaElementService, HTMLBackgroundService } from '@newroom-connect/library/services';
import { IFile, IExtendedMouseEvent, IAreaElement } from '@newroom-connect/library/interfaces';
import { FileHelper, DomHelper } from '@newroom-connect/library/helpers';
import { TranslatePipe } from '@newroom-connect/library/pipes';
import { fadeInOutAnimation } from '@newroom-connect/library/animations';

import { DestroyableComponent } from '../../../abstract/destroyable/destroyable.component';

export enum NrcHtmlSelector {
  LABEL = 'nrc-label',
  CONTENT = 'nrc-content'
}

@Component({
  selector: 'nrc-html-background',
  standalone: true,
  imports: [TranslatePipe],
  animations: [fadeInOutAnimation],
  templateUrl: './html-background.component.html',
  styleUrls: ['./html-background.component.scss']
})
export class HtmlBackgroundComponent extends DestroyableComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly fileService = inject(FileService);
  private readonly loggingService = inject(LoggingService);
  private readonly renderer = inject(Renderer2);
  private readonly areaElementService = inject(AreaElementService);
  protected readonly htmlBackgroundService = inject(HTMLBackgroundService);

  @ViewChild('backgroundContainer') public backgroundContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('placeholderTemplate') private placeholderTemplate?: TemplateRef<HTMLElement>;

  @Input({ required: true }) public apiBaseUrl = '';
  @Input({ required: true }) public backgroundFile!: IFile;
  @Input() public areaElements?: IAreaElement[];

  @Output() public loadComplete = new EventEmitter<void>();
  @Output() public areaElementMetadataChanged = new EventEmitter<IAreaElement>();

  public iframeElement: HTMLIFrameElement | null = null;
  private mutationObserver?: MutationObserver | null;
  private styleMutationObserver?: MutationObserver | null;
  private injectedTailwindStyleTags: HTMLStyleElement[] = [];

  constructor() {
    super();

    // Watch for area element selection changes to apply highlight border
    effect(() => {
      const selectedAreaElement = this.areaElementService.areaElementSelectedSig();
      const contentDocument = this.iframeElement?.contentDocument;

      if (!contentDocument || !this.areaElements || !selectedAreaElement) {
        return;
      }

      this.toggleSelectionHighlight(contentDocument, this.areaElements, selectedAreaElement);
    });

    effect(() => {
      const isEditingLabels = this.htmlBackgroundService.isEditingLabels();

      const labeledElements = (this.areaElements ?? []).reduce<HTMLElement[]>((accumulator, areaElement) => {
        if (areaElement.metadata?.htmlElementId && this.iframeElement?.contentDocument) {
          const labeledElement = this.iframeElement.contentDocument.getElementById(areaElement.metadata.htmlElementId);

          if (labeledElement) {
            accumulator.push(labeledElement);
          }
        }

        return accumulator;
      }, []);

      for (const labeledElement of labeledElements) {
        this.applyLabelEditMode(labeledElement, isEditingLabels);
      }
    });
  }

  /**
   *
   */
  public ngAfterViewInit(): void {
    this.loadBackgroundHtml(this.backgroundFile);
  }

  /**
   * Reload HTML content when the file changes.
   * Re-detect trigger elements when areaElements changes.
   *
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['backgroundFile'] && this.backgroundContainer) {
      const currentFile = changes['backgroundFile'].currentValue;
      const previousFile = changes['backgroundFile'].previousValue;

      // Only reload if it's actually a different file
      if (currentFile && (!previousFile || currentFile.id !== previousFile.id)) {
        this.clearContent();
        this.loadBackgroundHtml(currentFile);
      }
    }
  }

  /**
   * Cleanup mutation observer and remove any active highlight border on component destruction.
   */
  public override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.htmlBackgroundService.setLabelEditMode(false);

    this.cleanupMutationObserver();

    this.cleanupStyleMonitoring();

    // Unregister content document from placeholder service
    this.htmlBackgroundService.unregisterContentDocument();

    const selectedAreaElement = this.areaElementService.areaElementSelectedSig();

    // Remove highlight border from active element
    if (selectedAreaElement?.metadata?.htmlElementId && this.iframeElement?.contentDocument) {
      const activeElement = this.iframeElement.contentDocument.getElementById(selectedAreaElement?.metadata?.htmlElementId);

      if (activeElement) {
        activeElement.style.border = '';
      }
    }
  }

  /**
   * Loads the HTML background file.
   *
   * @param backgroundFile The file containing the HTML content.
   */
  public loadBackgroundHtml(backgroundFile: IFile): void {
    const sourceUrl = FileHelper.getFileSourceURI(this.apiBaseUrl, backgroundFile);

    this.fileService.getFileContent(sourceUrl).pipe(take(1)).subscribe({
      next: result => {
        this.loggingService.debug('File Content available:', result);
        if (!this.backgroundContainer) {
          return;
        }

        this.loadContent(result);
      },
      error: err => {
        this.loggingService.error('Error loading file content:', err);
      }
    });
  }

  /**
   * Clears the existing HTML content from the background container.
   */
  private clearContent(): void {
    if (this.iframeElement && this.backgroundContainer) {
      this.backgroundContainer.nativeElement.removeChild(this.iframeElement);
      this.iframeElement = null;
    }

    this.cleanupMutationObserver();
  }

  /**
   * Loads the content of the HTML file into an iframe or object tag.
   *
   * @param htmlContent The HTML content to load.
   */
  private loadContent(htmlContent: string): void {
    this.iframeElement = this.renderer.createElement('iframe');

    if (!this.iframeElement || !this.backgroundContainer) {
      return;
    }

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobURL = URL.createObjectURL(blob);

    this.iframeElement.onload = (): void => {
      URL.revokeObjectURL(blobURL);

      // Register content document with placeholder service
      if (this.iframeElement?.contentDocument) {
        this.htmlBackgroundService.registerContentDocument(this.iframeElement.contentDocument);
      }

      // Inject application stylesheet for Tailwind and other styles
      this.injectStylesheet();

      // Setup monitoring for dynamically added styles (e.g., VideoJS)
      this.setupStyleMonitoring();

      // Inject border trails animation keyframes into the content document
      this.injectBorderTrailsAnimation();

      // Setup click listener to handle propagation of click events to the parent document.
      this.setupObjectClickListener();

      // Initial detection for content elements
      this.detectContentElements();

      // Setup mutation observer to detect dynamically added label elements
      this.setupMutationObserver();

      // Configure iframe classes and attributes
      this.setIframeConfiguration();

      this.loadComplete.emit();
    };

    this.iframeElement.src = blobURL;

    this.backgroundContainer.nativeElement.appendChild(this.iframeElement);
  }

  /**
   * Sets the iframe classes and attributes for allowing videos to be displayed in fullscreen.
   */
  private setIframeConfiguration():void {
    if (!this.iframeElement) {
      return;
    }

    this.iframeElement.classList.add('w-full');
    this.iframeElement.classList.add('h-full');
    this.iframeElement.classList.add('aspect-video');

    this.renderer.setAttribute(this.iframeElement, 'allowfullscreen', 'true');
    this.renderer.setAttribute(this.iframeElement, 'webkitallowfullscreen', 'true');
    this.renderer.setAttribute(this.iframeElement, 'mozallowfullscreen', 'true');
    this.renderer.setAttribute(this.iframeElement, 'allow', 'fullscreen');
  }

  /**
   * Sets up click event listener for the object element's content document.
   */
  private setupObjectClickListener(): void {
    const contentDocument = this.iframeElement?.contentDocument;

    if (!contentDocument) {
      return;
    }

    contentDocument.addEventListener('click', () => {
      // Simulate a click event on the parent document loading the HTML content.
      const newEvent: IExtendedMouseEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });

      newEvent.isSourceEmbedded = true;
      document.dispatchEvent(newEvent);
    });
  }

  /**
   * Detects all elements with nrc-content attribute in the loaded HTML content
   * and injects placeholder content using the HTMLBackgroundService.
   */
  private detectContentElements(): void {
    if (!this.placeholderTemplate) {
      this.loggingService.warn('Placeholder template not available');

      return;
    }

    // Use the service to inject content into all [nrc-content] elements
    this.htmlBackgroundService.showContentInPlaceholders(this.placeholderTemplate);
  }

  /**
   * Applies label edit mode styling and behavior to a specific HTML element.
   * This includes toggling contentEditable, cursor style, border-trail animation, and disabling buttons.
   *
   * @param htmlElement The HTML element containing the label.
   * @param isEditingLabels Whether edit mode is active.
   */
  private applyLabelEditMode(htmlElement: HTMLElement, isEditingLabels: boolean): void {
    const labelElement = htmlElement.querySelector<HTMLElement>(`[${NrcHtmlSelector.LABEL}]`);

    if (!labelElement) {
      return;
    }

    // Disable possible button elements to prevent unwanted clicks during edit process
    if (DomHelper.isButtonElement(htmlElement)) {
      htmlElement.disabled = isEditingLabels;
    }

    // Toggle `Edit` mode for nrc-label elements. Disables the default click event and highlights editable labels.
    labelElement.contentEditable = isEditingLabels ? 'true' : 'false';
    labelElement.style.cursor = isEditingLabels ? 'text' : 'default';

    // Apply border trail animation class
    if (isEditingLabels) {
      labelElement.classList.add('border-trail-active');
    } else {
      labelElement.classList.remove('border-trail-active');
    }
  }

  /**
   * Initializes a single label element by setting up toggle button and event listeners.
   * This method can be used for both static and dynamically created elements.
   *
   * @param htmlElement The HTML element to initialize as an editable label.
   */
  private initializeLabelElement(htmlElement: HTMLElement): void {
    // Apply stored label value if available
    if (!this.areaElements) {
      return;
    }

    for (const areaElement of this.areaElements) {
      if (htmlElement.id !== areaElement.metadata?.htmlElementId) {
        continue;
      }

      const labelElement = htmlElement.querySelector<HTMLElement>(`[${NrcHtmlSelector.LABEL}]`);

      if (!labelElement) {
        continue;
      }

      if (!areaElement.metadata?.htmlElementLabel) {
        areaElement.metadata.htmlElementLabel = labelElement.innerText;
      }

      labelElement.innerText = areaElement.metadata.htmlElementLabel;
    }

    // Add input event listener with debouncing for real-time updates
    fromEvent(htmlElement, 'input')
      .pipe(
        debounceTime(200),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.areaElements) {
          return;
        }

        for (const areaElement of this.areaElements) {
          if (htmlElement.id !== areaElement.metadata?.htmlElementId) {
            continue;
          }

          const labelElement = htmlElement.querySelector<HTMLElement>(`[${NrcHtmlSelector.LABEL}]`);

          if (!labelElement) {
            continue;
          }

          areaElement.metadata.htmlElementLabel = labelElement.innerText;

          // Emit the change so parent components can update their signals
          this.areaElementMetadataChanged.emit(areaElement);
        }
      });
  }

  /**
   * Sets up a MutationObserver to detect dynamically added and removed elements with nrcdata-label attribute.
   * This allows the component to handle elements created or removed after the initial page load.
   */
  private setupMutationObserver(): void {
    const contentDocument = this.iframeElement?.contentDocument;

    if (!contentDocument) {
      return;
    }

    // Cleanup existing observer if any
    this.cleanupMutationObserver();

    // Create new mutation observer
    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Check added nodes for label and content elements
        for (const node of Array.from(mutation.addedNodes)) {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            continue;
          }

          const htmlElement = node as HTMLElement;

          // Check if the added element itself has the label attribute
          if (this.isActionElement(htmlElement, this.areaElements)) {
            this.initializeLabelElement(htmlElement);

            // Apply current edit mode state to the newly added element
            const isEditingLabels = this.htmlBackgroundService.isEditingLabels();

            this.applyLabelEditMode(htmlElement, isEditingLabels);
          }

          // Check if the added element itself has the content attribute
          if (this.placeholderTemplate && htmlElement.hasAttribute(NrcHtmlSelector.CONTENT)) {
            this.htmlBackgroundService.showContentInPlaceholders(this.placeholderTemplate);
          }
        }
      }
    });

    // Start observing the content document body for child additions and removals
    this.mutationObserver.observe(contentDocument.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Cleans up the mutation observer if it exists.
   */
  private cleanupMutationObserver(): void {
    if (!this.mutationObserver) {
      return;
    }

    this.mutationObserver.disconnect();
    this.mutationObserver = null;
  }

  /**
   * Sets up monitoring for dynamically added style tags in the parent document.
   * This ensures styles added after initial load (e.g., VideoJS) are copied to contentDocument.
   */
  private setupStyleMonitoring(): void {
    const contentDocument = this.iframeElement?.contentDocument;

    if (!contentDocument) {
      return;
    }

    // Cleanup existing observer if any
    this.cleanupStyleMonitoring();

    // Create mutation observer to watch for new style tags
    this.styleMutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          // Check if a new style tag was added
          if (node.nodeName === 'STYLE') {
            this.cloneStyleToContentDocument(node as HTMLStyleElement, contentDocument);
          }
        }
      }
    });

    // Start observing the parent document's head for new style tags
    this.styleMutationObserver.observe(document.head, {
      childList: true
    });
  }

  /**
   * Clones a style tag from the parent document to the content document.
   * If the style appears to be from VideoJS, re-injects Tailwind styles after it to maintain proper cascade order.
   *
   * @param styleTag The HTMLStyleElement to be cloned.
   * @param contentDocument The target document where the style will be appended.
   */
  private cloneStyleToContentDocument(styleTag: HTMLStyleElement, contentDocument: Document): void {
    const clonedStyle = styleTag.cloneNode(true) as HTMLStyleElement;

    contentDocument.head.appendChild(clonedStyle);

    // Check if this is a VideoJS style tag (contains vjs classes or dimensions)
    const isVideoJSStyle = styleTag.textContent?.includes('vjs') || styleTag.textContent?.includes('video-js');

    if (isVideoJSStyle && this.injectedTailwindStyleTags.length > 0) {
      this.reInjectTailwindStyles(contentDocument);
    }
  }

  /**
   * Re-injects Tailwind style tags to ensure they come after VideoJS styles in the cascade.
   * This maintains proper specificity so Tailwind utilities override VideoJS dimensions.
   *
   * @param contentDocument The target document where styles will be re-injected.
   */
  private reInjectTailwindStyles(contentDocument: Document): void {
    // Remove existing Tailwind style tags from contentDocument
    for (const styleTag of this.injectedTailwindStyleTags) {
      if (styleTag.parentNode) {
        styleTag.parentNode.removeChild(styleTag);
      }
    }

    // Clear the tracking array
    this.injectedTailwindStyleTags = [];

    // Re-inject Tailwind styles (they'll now come after VideoJS styles)
    // 1. Clone inline style tags
    const parentStyles = document.head.querySelectorAll('style');

    parentStyles.forEach((styleTag) => {
      const clonedStyle = styleTag.cloneNode(true) as HTMLStyleElement;

      contentDocument.head.appendChild(clonedStyle);
      this.injectedTailwindStyleTags.push(clonedStyle);
    });

    // 2. Extract CSS from linked stylesheets
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        const cssRules = Array.from(sheet.cssRules || []);
        const cssText = cssRules.map(rule => rule.cssText).join('\n');

        const styleTag = contentDocument.createElement('style');

        styleTag.textContent = cssText;
        contentDocument.head.appendChild(styleTag);
        this.injectedTailwindStyleTags.push(styleTag);
      } catch (error) {
        // CORS may block access to some stylesheets
        this.loggingService.warn('Could not access stylesheet:', sheet.href, error);
      }
    });
  }

  /**
   * Cleans up the style mutation observer if it exists.
   */
  private cleanupStyleMonitoring(): void {
    if (!this.styleMutationObserver) {
      return;
    }

    this.styleMutationObserver.disconnect();
    this.styleMutationObserver = null;
  }

  /**
   * Injects the application stylesheet into the content document.
   * This ensures Tailwind classes and other global styles are available for injected content.
   * Tracks injected style tags for potential re-injection after VideoJS styles.
   */
  private injectStylesheet(): void {
    const contentDocument = this.iframeElement?.contentDocument;

    if (!contentDocument) {
      return;
    }

    // Clear tracking array
    this.injectedTailwindStyleTags = [];

    // 1. Clone inline style tags
    const parentStyles = document.head.querySelectorAll('style');

    parentStyles.forEach((styleTag) => {
      const clonedStyle = styleTag.cloneNode(true) as HTMLStyleElement;

      contentDocument.head.appendChild(clonedStyle);
      this.injectedTailwindStyleTags.push(clonedStyle);
    });

    // 2. Extract CSS from linked stylesheets
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        // Get CSS rules from the stylesheet
        const cssRules = Array.from(sheet.cssRules || []);
        const cssText = cssRules.map(rule => rule.cssText).join('\n');

        // Create a style tag with the CSS content
        const styleTag = contentDocument.createElement('style');

        styleTag.textContent = cssText;
        contentDocument.head.appendChild(styleTag);
        this.injectedTailwindStyleTags.push(styleTag);
      } catch (error) {
        // CORS may block access to some stylesheets
        this.loggingService.warn('Could not access stylesheet:', sheet.href, error);
      }
    });
  }

  /**
   * Injects the animated gradient border styles into the content document.
   */
  private injectBorderTrailsAnimation(): void {
    const contentDocument = this.iframeElement?.contentDocument;

    if (!contentDocument) {
      return;
    }

    const styleElement = contentDocument.createElement('style');

    styleElement.textContent = `
      @property --border-angle {
        inherits: false;
        initial-value: 0deg;
        syntax: '<angle>';
      }

      .border-trail-active {
        position: relative;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 4px;
        padding: 4px;
      }

      .border-trail-active::before {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 4px;
        padding: 1px;
        background: conic-gradient(
          from var(--border-angle),
          rgba(255, 255, 255, 0.1) 0%,
          rgba(0, 255, 255, 0.8) 15%,
          rgba(0, 200, 255, 1) 25%,
          rgba(0, 255, 255, 0.8) 35%,
          rgba(255, 255, 255, 0.1) 50%,
          rgba(255, 255, 255, 0.1) 100%
        );
        -webkit-mask: 
          linear-gradient(#fff 0 0) content-box, 
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        animation: rotateBorder 4s linear infinite;
        pointer-events: none;
        z-index: -1;
      }

      @keyframes rotateBorder {
        to {
          --border-angle: 360deg;
        }
      }
    `;

    contentDocument.head.appendChild(styleElement);
  }

  /**
   *
   * @param htmlElement
   * @param areaElements
   * @returns
   */
  private isActionElement(htmlElement: HTMLElement, areaElements?: IAreaElement[]): boolean {
    if (!areaElements) {
      return false;
    }

    return areaElements.some((areaElement) => htmlElement.id && areaElement.metadata?.htmlElementId === htmlElement.id);
  }

  /**
   * Toggles the highlight border between area elements by removing it from all elements
   * and applying it only to the currently selected element.
   *
   * @param contentDocument - The Document object containing the HTML elements to highlight.
   * @param areaElements - Array of all area elements that may have borders to clear.
   * @param selectedAreaElement - The currently selected area element that should receive the highlight border.
   */
  private toggleSelectionHighlight(contentDocument: Document, areaElements: IAreaElement[], selectedAreaElement: IAreaElement): void {
    // Remove border from previously active element

    for (const areaElement of areaElements) {
      if (!areaElement.metadata?.htmlElementId) {
        continue;
      }

      const htmlElement = contentDocument.getElementById(areaElement.metadata.htmlElementId);

      if (htmlElement) {
        htmlElement.style.border = '';
      }
    }

    // Apply cyan border to new element if it has metadata.htmlElementId
    if (selectedAreaElement?.metadata?.htmlElementId) {
      const htmlElement = contentDocument.getElementById(selectedAreaElement.metadata.htmlElementId);

      if (htmlElement) {
        htmlElement.style.border = '3px solid cyan';
      }
    }
  }
}
